'use client';

// BUG-1798 — keeps list URL writes from cancelling an in-flight router navigation.
//
// List state is written with `history.replaceState` (BL-1726). Next's app
// router patches that method to dispatch a RESTORE, and a RESTORE dispatched
// while a NAVIGATE is pending marks the navigation discarded — so a debounced
// term write that lands ~300 ms after the operator clicked a link (or switched
// tenant) silently cancels the click.
//
// `instrumentation-client.ts` calls `beginRouterNavigation` from Next's
// `onRouterTransitionStart`, synchronously BEFORE the navigate is dispatched:
//
//   - push / replace: every unsettled term is written NOW, while the location
//     is still this page's (so Back returns to the list with the operator's
//     term), then the page is marked as being left and `useUrlParam` writes
//     nothing until the location moves.
//   - traverse (Back / Forward): the browser location has already moved, so a
//     write would stamp this list's path onto the destination entry; unsettled
//     terms are dropped instead.
//
// State lives on `globalThis` behind a `Symbol.for` key so the instrumentation
// bundle and the page bundles share one registry even if the module is
// evaluated twice.

/** A debounced list write that has not reached the URL yet. */
export interface PendingListWrite {
    /** Write it now, before the navigation is dispatched. */
    flush: () => void;
    /** Forget it — the page is being left by Back / Forward. */
    drop: () => void;
}

interface NavigationGuardState {
    pending: Set<PendingListWrite>;
    /** Set while a push / replace is in flight: the location it started from. */
    leaving: { fromHref: string; at: number } | null;
}

/**
 * A navigation that never commits (a same-URL push, a failed fetch) must not
 * mute list writes forever; past this the mark is treated as stale.
 */
const LEAVING_TTL_MS = 15_000;
const STATE_KEY = Symbol.for('auctogon.admin.list-url-navigation');

function state(): NavigationGuardState {
    const g = globalThis as typeof globalThis & { [STATE_KEY]?: NavigationGuardState };
    g[STATE_KEY] ??= { pending: new Set(), leaving: null };
    return g[STATE_KEY];
}

/**
 * Register a debounced list write that has not reached the URL yet, so a
 * navigation can flush or drop it before it lands mid-flight.
 *
 * @param write - How to write it now, and how to forget it.
 * @returns Unregister — call once the write has landed or the owner unmounts.
 */
export function registerPendingListWrite(write: PendingListWrite): () => void {
    const { pending } = state();
    pending.add(write);
    return () => {
        pending.delete(write);
    };
}

/**
 * Write every unsettled list term to the URL now. A click handler that reads
 * `window.location` to build a link (a row's back link) calls this first, so the
 * link carries the term the operator typed inside the debounce window
 * (BUG-2189); `beginRouterNavigation` calls it for the same reason on push /
 * replace. Each write is marked written, so the debounce has nothing left to say.
 *
 * @returns Nothing.
 *
 * @sideEffect May write unsettled list terms to the URL via `history.replaceState`.
 */
export function flushPendingListWrites(): void {
    const { pending } = state();
    const writes = [...pending];
    pending.clear();
    for (const write of writes) write.flush();
}

/**
 * The page's URL with every unsettled list term already written into it. The one
 * way a click handler reads the list's query to build a link or a router URL (a
 * row's back link, the Inventory `?item=` write), so no handler can read the URL
 * before the search debounce lands (BUG-2189, BL-2541, BUG-2359).
 *
 * @returns A fresh `URL` of the current location, after the flush.
 *
 * @sideEffect May write unsettled list terms to the URL via `history.replaceState`.
 */
export function readFlushedListUrl(): URL {
    flushPendingListWrites();
    return new URL(window.location.href);
}

/**
 * A router navigation is about to be dispatched. Flush (push / replace) or
 * drop (traverse) every unsettled list write, then — for push / replace to a
 * different URL — mark the page as being left until the location moves.
 *
 * @param type - Next's navigation type: `'push'`, `'replace'` or `'traverse'`.
 * @param url - The destination, as Next hands it (relative or absolute).
 * @returns Nothing.
 *
 * @sideEffect May write unsettled list terms to the URL via `history.replaceState`.
 */
export function beginRouterNavigation(type: string, url?: string): void {
    const s = state();
    const traversing = type === 'traverse';
    if (traversing) {
        const writes = [...s.pending];
        s.pending.clear();
        for (const write of writes) write.drop();
    } else {
        flushPendingListWrites();
    }
    s.leaving = traversing || typeof window === 'undefined' || isCurrentHref(url)
        ? null
        : { fromHref: window.location.href, at: Date.now() };
}

/**
 * A push to the URL already on screen (a sidebar link to the page you are on)
 * changes no location, so a "leaving" mark would never clear and would mute
 * every list write until it went stale.
 */
function isCurrentHref(url: string | undefined): boolean {
    if (url == null) return false;
    try {
        return new URL(url, window.location.href).href === window.location.href;
    } catch {
        // An unparseable destination is not the current page; keep the guard.
        return false;
    }
}

/**
 * Whether a list URL write now would cancel an in-flight navigation. Clears
 * itself once the location moves (the navigation committed) or the mark goes
 * stale.
 *
 * @returns True while a push / replace started from this location is in flight.
 */
export function isLeavingPage(): boolean {
    const s = state();
    if (!s.leaving) return false;
    const moved = typeof window !== 'undefined' && window.location.href !== s.leaving.fromHref;
    if (moved || Date.now() - s.leaving.at > LEAVING_TTL_MS) {
        s.leaving = null;
        return false;
    }
    return true;
}
